import React, { Component } from 'react';
import PropTypes from 'prop-types';
import _ from 'lodash';
import { reduxForm, Field, propTypes, change, untouch } from 'redux-form';
import { LinkContainer } from 'react-router-bootstrap';
import { stringToBoolean } from 'deep-cuts';
import { PAYMENT_FORM } from '../redux/modules/form';
import { NewAccountFields, NewPaymentFields } from './NewAccountForm';
import { ConfirmDeleteSavedAccount } from './ConfirmationModals';
import paymentValidation from './validation/paymentValidation';
import ValidationSelect from './common/ValidationSelect';
import { newAccountFormTestDataIfRequested } from '../utils/testData';
import { ensureBankNameUnique } from '../utils/validation';
import { stateFromAbbreviation } from '../constants/general';
import { ACCOUNT_TYPE_OPTIONS, DEPOSIT_OPTIONS } from '../constants/payment';
import * as storage from '../utils/storage';

function savedAccountOptions(user) {
  return _.chain(user.savedAccounts)
    .filter(_.property('sageApiToken'))
    .map(_.property('bankName'))
    .compact()
    .uniq()
    .value();
}

function SavedAccountLabel({ /* onNewAccount, */ onRemoveAccount, selectedSavedAccount }) {
  // eslint-disable-line no-unused-vars
  return (
    <span>
      <div>Saved Accounts</div>
      <div className="success-redirect-msg">
        (
        <LinkContainer className="payment-form-component-save-account-link" to="/saveAccount">
          <a>New Account</a>
        </LinkContainer>
        ) (
        <ConfirmDeleteSavedAccount onConfirm={onRemoveAccount} bankAccount={selectedSavedAccount}>
          <a>
            <span
              style={{
                color: '#932525',
                fontSize: '18px',
              }}
            >
              x
            </span>
          </a>
        </ConfirmDeleteSavedAccount>
        )
      </div>
    </span>
  );
}

SavedAccountLabel.propTypes = {
  // onNewAccount: PropTypes.func.isRequired,
  onRemoveAccount: PropTypes.func.isRequired,
  selectedSavedAccount: PropTypes.string,
};

SavedAccountLabel.defaultProps = {
  selectedSavedAccount: null,
};

function SavedAccountsSelectors({ user, selectedSavedAccount, onNewAccount, onRemoveAccount }) {
  if (_.get(user, 'savedAccounts.length')) {
    return (
      <Field
        name="savedAccount"
        component={ValidationSelect}
        label={
          <SavedAccountLabel
            selectedSavedAccount={selectedSavedAccount}
            onNewAccount={onNewAccount}
            onRemoveAccount={onRemoveAccount}
          />
        }
        options={savedAccountOptions(user)}
      />
    );
  } else {
    return null;
  }
}

SavedAccountsSelectors.propTypes = {
  user: PropTypes.object.isRequired,
  onNewAccount: PropTypes.func.isRequired,
  onRemoveAccount: PropTypes.func.isRequired,
  selectedSavedAccount: PropTypes.string,
};

SavedAccountsSelectors.defaultProps = {
  selectedSavedAccount: null,
};

function loadDealerInfo({ user, dispatch }) {
  if (!_.isNil(user)) {
    // Auto-fill address from user (dealership) profile
    const { dealerInfo } = user;
    dispatch(change(PAYMENT_FORM, 'companyName', _.get(dealerInfo, 'dealerName')));
    dispatch(change(PAYMENT_FORM, 'address', _.get(dealerInfo, 'dealerAddress')));
    dispatch(change(PAYMENT_FORM, 'city', _.get(dealerInfo, 'dealerCity')));
    dispatch(
      change(PAYMENT_FORM, 'state', stateFromAbbreviation(_.get(dealerInfo, 'dealerState'))),
    );
    dispatch(change(PAYMENT_FORM, 'zipCode', _.get(dealerInfo, 'dealerZip')));
    dispatch(change(PAYMENT_FORM, 'accountType', ACCOUNT_TYPE_OPTIONS.CHECKING));
    dispatch(change(PAYMENT_FORM, 'depositType', DEPOSIT_OPTIONS.STATEMENT));

    // Auto-select first saved account
    if (_.get(user, 'savedAccounts.length')) {
      dispatch(change(PAYMENT_FORM, 'savedAccount', _.get(user, 'savedAccounts[0].bankName')));
      dispatch(untouch(PAYMENT_FORM, 'savedAccount'));
    }
  }
}

export class PaymentForm extends Component {
  static propTypes = {
    ...propTypes,
    error: PropTypes.string,
    isNewAccount: PropTypes.bool,
    selectedSavedAccount: PropTypes.string,
    bankDetails: PropTypes.object,
    disableAmount: PropTypes.bool,
    disableSubmit: PropTypes.bool,
    user: PropTypes.object.isRequired,
    onNotifyUserOfPayment: PropTypes.func,
    onNewAccount: PropTypes.func,
    onChangeRoutingNumber: PropTypes.func,
    onZipCodeChange: PropTypes.func,
    onRemoveAccount: PropTypes.func,
  };

  static defaultProps = {
    error: null,
    selectedSavedAccount: null,
    bankDetails: null,
    disableAmount: false,
    disableSubmit: false,
    isNewAccount: false,
    onNotifyUserOfPayment: _.noop,
    onNewAccount: _.noop,
    onChangeRoutingNumber: _.noop,
    onZipCodeChange: _.noop,
    onRemoveAccount: _.noop,
  };

  componentDidMount = () => {
    const { user, dispatch, onNotifyUserOfPayment } = this.props;

    // Pull any payment values provided from a query
    const paymentQuery = storage.get('paymentQuery');
    const payment = _.get(paymentQuery, 'payment');
    const contractNumbers = _.get(paymentQuery, 'contractNumbers');
    const amounts = _.get(paymentQuery, 'amounts');
    const comments = _.get(paymentQuery, 'comments');
    const nsfComment = _.get(paymentQuery, 'nsfComment');
    const customComment = _.get(paymentQuery, 'customComment');
    if (payment) {
      dispatch(change(PAYMENT_FORM, 'amount', payment));
    }
    if (contractNumbers) {
      dispatch(change(PAYMENT_FORM, 'contractNumbers', contractNumbers));
    }
    if (amounts) {
      dispatch(change(PAYMENT_FORM, 'amounts', amounts));
    }
    if (comments && stringToBoolean(customComment)) {
      dispatch(change(PAYMENT_FORM, 'memo', comments));
    }

    // Notify the user
    onNotifyUserOfPayment({
      payment,
      contractNumbers,
      amounts,
      nsfComment: stringToBoolean(nsfComment),
    });

    // Load dealer info if necessary
    loadDealerInfo({
      user,
      dispatch,
    });
  };

  componentWillReceiveProps = (nextProps) => {
    const { dispatch, bankDetails } = nextProps;
    if (
      (_.isEmpty(bankDetails) && !_.isEmpty(nextProps.bankDetails)) ||
      !_.matches(bankDetails)(nextProps.bankDetails)
    ) {
      dispatch(change(PAYMENT_FORM, 'bankName', _.get(nextProps.bankDetails, 'name')));
      dispatch(untouch(PAYMENT_FORM, 'bankName'));
    }
  };

  handleSubmitAndReset = (data) => {
    const { handleSubmit } = this.props;
    return handleSubmit(data);
  };

  render() {
    const {
      error,
      user,
      bankDetails,
      selectedSavedAccount,
      isNewAccount,
      disableSubmit,
      disableAmount,
      onRemoveAccount,
      onChangeRoutingNumber,
      onZipCodeChange,
      onNewAccount,
    } = this.props;

    return (
      <form className="form-horizontal" onSubmit={this.handleSubmitAndReset}>
        <SavedAccountsSelectors
          user={user}
          selectedSavedAccount={selectedSavedAccount}
          onNewAccount={onNewAccount}
          onRemoveAccount={onRemoveAccount}
        />
        {!_.get(user, 'savedAccounts.length') || isNewAccount ? (
          <NewAccountFields
            title="Billing Details (will be saved for future payments)"
            bankDetails={bankDetails}
            bankNameValidate={_.partial(ensureBankNameUnique, user)}
            onChangeRoutingNumber={onChangeRoutingNumber}
            onZipCodeChange={onZipCodeChange}
          />
        ) : undefined}
        <NewPaymentFields disableAmount={disableAmount} />
        {error && (
          <p className="text-danger">
            <strong>{error}</strong>
          </p>
        )}
        <div className="row">
          <div className="col-md-3 col-xs-12">
            <button className="btn btn-primary btn-block" type="submit" disabled={disableSubmit}>
              <i className="fa fa-money" /> Pay to PRco
            </button>
          </div>
        </div>
      </form>
    );
  }
}

export default reduxForm({
  form: PAYMENT_FORM,
  validate: paymentValidation,
  initialValues: newAccountFormTestDataIfRequested(),
})(PaymentForm);
